import React, { useCallback, useMemo } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { useIsBulkOperationsInProgress } from '@atlassian/jira-bulk-operations-common/src/controllers/use-bulk-operations/index.tsx';
import isShallowEqual from '@atlassian/jira-common-util-is-shallow-equal';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch';
import { BOARD } from '../../model/constants';
import { CardListContext } from '../../services/card-list-context';
import {
	scheduleCardListToggleFinish,
	setCardListToggleStart,
} from '../../services/performance/card-list-metrics';
import { SprintLifecycleContext } from '../../services/sprint-lifecycle-context';
import { useBacklogDispatch, useBacklogSelector } from '../../state';
import { cardListToggle } from '../../state/actions/card-list';
import { sprintCreateRequest } from '../../state/actions/sprints/create';
import {
	getIsCompanyManaged,
	isCardMoveDisabled,
	isCmpKanbanProject,
} from '../../state/selectors/app';
import {
	getCardLists,
	getFlattenOrderedCardIds,
	getOrderedCardIdsByCardListId,
	getOrderedCardIdsByCardListIdWithLimit,
	isAnyOptimisticCardList,
	hasCardTypesSelector,
	makeGetCardListAnalyticsTypeById,
	makeGetMinColumnLimit,
	makeGetMaxColumnLimit,
	makeIsCardListCollapsed,
	makeIsCardListDisabled,
	getIsDragging,
	getIsCardListDragInProgress,
	makeOrderedVisibleCardsExcludingSubtasksForCardListWithLimit,
	makeOrderedVisibleCardsExcludingSubtasksForCardList,
	makeOrderedCardIdsByCardListIdExcludingSubtasks,
	makeOrderedCardIdsByCardListIdWithLimitExcludingSubtasks,
} from '../../state/selectors/cards';
import { isInlineIssueCreateAvailableForCurrentUser } from '../../state/selectors/config';
import { getIsSubtaskDrag } from '../../state/selectors/drag';
import { canEditIssue as canEditIssueSelector } from '../../state/selectors/permissions';
import {
	isBacklogWithSprints,
	makeCanEditSprint,
	makeIsAddSprintDatesButtonVisible,
	makeIsCardListActiveSprint,
	makeIsCompleteSprintVisible,
	makeIsCreateSprintVisible,
	makeIsSprintMenuVisible,
	makeIsStartSprintEnabled,
	makeIsStartSprintVisible,
} from '../../state/selectors/sprints';
import { shouldShowTransitionZone } from './utils';
import CardList from './view';

export type Props = {
	id: string;
	isFirstList: boolean;
	isFirstNonEmptyList: boolean;
	isLastNonEmptyList: boolean;
	justCreated: boolean;
};

const CardListContainer = ({
	id,
	isFirstList,
	isFirstNonEmptyList,
	isLastNonEmptyList,
	justCreated,
}: Props) => {
	const canEditIssue = useBacklogSelector(canEditIssueSelector);
	const isBacklogEmpty = useBacklogSelector(
		(state) => getFlattenOrderedCardIds(state).length === 0,
	);
	const isActiveSprint = useBacklogSelector(makeIsCardListActiveSprint);
	const isIccEnabled = useBacklogSelector(
		(state) =>
			isInlineIssueCreateAvailableForCurrentUser(state) &&
			(getIsCompanyManaged(state) || hasCardTypesSelector(state)) &&
			// ICC is disabled for CMP kanban board card list or active sprint
			!(getIsCompanyManaged(state) && (id === BOARD || isActiveSprint(id))),
	);

	const isBulkOpsInProgress = useIsBulkOperationsInProgress();

	const isDisabled =
		useBacklogSelector((state) => makeIsCardListDisabled(state)(id)) || isBulkOpsInProgress;
	const isSprintMenuVisible = useBacklogSelector((state) => makeIsSprintMenuVisible(state)(id));
	const isCreateSprintButtonDisabled = useBacklogSelector(isAnyOptimisticCardList);
	const isCreateSprintButtonVisible = useBacklogSelector((state) =>
		makeIsCreateSprintVisible(state)(id),
	);
	const isStartSprintButtonVisible = useBacklogSelector((state) =>
		makeIsStartSprintVisible(state)(id, isFirstList),
	);
	const canEditSprint = useBacklogSelector((state) => makeCanEditSprint(state)(id));
	const isStartSprintEnabled = useBacklogSelector((state) =>
		makeIsStartSprintEnabled(state)(id, isFirstList),
	);
	const isCompleteSprintButtonVisible = useBacklogSelector((state) =>
		makeIsCompleteSprintVisible(state)(id),
	);

	const cardIds = useBacklogSelector((state) =>
		__SERVER__
			? getOrderedCardIdsByCardListIdWithLimit(state)[id]
			: getOrderedCardIdsByCardListId(state)[id],
	);
	const isAddSprintDatesButtonVisible = useBacklogSelector((state) =>
		makeIsAddSprintDatesButtonVisible(state, { cardListId: id }),
	);
	const isCollapsed = useBacklogSelector((state) =>
		__SERVER__
			? makeIsCardListCollapsed(state)(id) ||
				(!getOrderedCardIdsByCardListIdWithLimit(state)[id].length &&
					!!getOrderedCardIdsByCardListId(state)[id].length)
			: makeIsCardListCollapsed(state)(id),
	);
	const isWorkloadByAssigneeEnabled = useBacklogSelector(isBacklogWithSprints);

	const isDraggingSubtask = useBacklogSelector(getIsSubtaskDrag);

	const isCmpKanbanBacklog = useBacklogSelector(isCmpKanbanProject);

	const allCardIds = useBacklogSelector((state) => getOrderedCardIdsByCardListId(state)[id]);
	const cardListName = useBacklogSelector((state) => getCardLists(state)[id].name);
	const cardListType = useBacklogSelector((state) => getCardLists(state)[id].type);
	const typeForAnalytics = useBacklogSelector((state) =>
		makeGetCardListAnalyticsTypeById(state)(id),
	);
	const cardListSprintData = useBacklogSelector(
		(state) => getCardLists(state)[id].sprintData,
		isShallowEqual,
	);

	const isDragging = useBacklogSelector(getIsDragging);
	const isCurrentCardListDragSource = useBacklogSelector((state) =>
		isDragging ? getIsCardListDragInProgress(state, id) : false,
	);
	const cardMoveDisabled = useBacklogSelector(isCardMoveDisabled);

	const minColumnLimit = useBacklogSelector((state) => makeGetMinColumnLimit(state)(id));
	const maxColumnLimit = useBacklogSelector((state) => makeGetMaxColumnLimit(state)(id));
	const isMinLimitReached = !!minColumnLimit && minColumnLimit > allCardIds.length;
	const isMaxLimitReached = !!maxColumnLimit && maxColumnLimit < allCardIds.length;

	const dispatch = useBacklogDispatch();

	const onToggle = useCallback(() => {
		setCardListToggleStart();
		dispatch(cardListToggle(id));
		scheduleCardListToggleFinish();
	}, [dispatch, id]);

	const onCreateSprint = useCallback(
		(analyticsEvent: UIAnalyticsEvent) => {
			dispatch(sprintCreateRequest(analyticsEvent));
		},
		[dispatch],
	);

	const showTransitionZone = shouldShowTransitionZone(
		isDragging,
		isCurrentCardListDragSource,
		cardMoveDisabled,
		isDraggingSubtask,
		isCmpKanbanBacklog,
	);

	const getCardOrderExcludingSubtasks = useBacklogSelector((state) =>
		__SERVER__
			? makeOrderedVisibleCardsExcludingSubtasksForCardListWithLimit(state)
			: makeOrderedVisibleCardsExcludingSubtasksForCardList(state),
	);

	const getTotalCardCountExcludingSubtasks = useBacklogSelector((state) =>
		__SERVER__
			? makeOrderedCardIdsByCardListIdWithLimitExcludingSubtasks(state)
			: makeOrderedCardIdsByCardListIdExcludingSubtasks(state),
	);

	const sprintLifecycleContextValue = useMemo(() => {
		return {
			canEditSprint,
			isAddSprintDatesButtonVisible,
			isCompleteSprintButtonVisible,
			isCreateSprintButtonDisabled,
			isSprintMenuVisible,
			isStartSprintButtonVisible,
			isStartSprintEnabled,
		};
	}, [
		canEditSprint,
		isAddSprintDatesButtonVisible,
		isCompleteSprintButtonVisible,
		isCreateSprintButtonDisabled,
		isSprintMenuVisible,
		isStartSprintButtonVisible,
		isStartSprintEnabled,
	]);

	const cardListContextValue = useMemo(() => {
		return {
			cardCountExcludingSubtasks: getTotalCardCountExcludingSubtasks(id).length,
			cardIds,
			cardListId: id,
			isCollapsed,
			isFirstList,
			isMaxLimitReached,
			isMinLimitReached,
			maxColumnLimit: maxColumnLimit ?? undefined,
			minColumnLimit: minColumnLimit ?? undefined,
			listName: cardListName,
			sprintData: cardListSprintData,
			type: cardListType,
			visibleCardAmount: getCardOrderExcludingSubtasks(id).length,
		};
	}, [
		cardIds,
		cardListName,
		cardListSprintData,
		cardListType,
		getCardOrderExcludingSubtasks,
		getTotalCardCountExcludingSubtasks,
		id,
		isCollapsed,
		isFirstList,
		isMaxLimitReached,
		isMinLimitReached,
		maxColumnLimit,
		minColumnLimit,
	]);

	// Clean up when visual refresh is removed
	const content = (
		<CardList
			cardListId={id}
			type={cardListType}
			typeForAnalytics={typeForAnalytics}
			name={cardListName}
			sprintData={cardListSprintData}
			canEditIssue={canEditIssue}
			canEditSprint={canEditSprint}
			isBacklogEmpty={isBacklogEmpty}
			isFirstList={isFirstList}
			isFirstNonEmptyList={isFirstNonEmptyList}
			isLastNonEmptyList={isLastNonEmptyList}
			justCreated={justCreated}
			isIccEnabled={isIccEnabled}
			isDisabled={isDisabled}
			isCollapsed={isCollapsed}
			isActiveSprint={isActiveSprint(id)}
			isSprintMenuVisible={isSprintMenuVisible}
			isCreateSprintButtonDisabled={isCreateSprintButtonDisabled}
			isCreateSprintButtonVisible={isCreateSprintButtonVisible}
			isStartSprintButtonVisible={isStartSprintButtonVisible}
			isCompleteSprintButtonVisible={isCompleteSprintButtonVisible}
			isAddSprintDatesButtonVisible={isAddSprintDatesButtonVisible}
			cardIds={cardIds}
			allCardIds={allCardIds}
			cardCountExcludingSubtasks={getTotalCardCountExcludingSubtasks(id).length}
			visibleCardAmount={getCardOrderExcludingSubtasks(id).length}
			onToggle={onToggle}
			onCreateSprint={onCreateSprint}
			isStartSprintEnabled={isStartSprintEnabled}
			showTransitionZone={showTransitionZone}
			minColumnLimit={minColumnLimit}
			maxColumnLimit={maxColumnLimit}
			isWorkloadByAssigneeEnabled={isWorkloadByAssigneeEnabled}
		/>
	);

	if (isVisualRefreshEnabled()) {
		return (
			<SprintLifecycleContext.Provider value={sprintLifecycleContextValue}>
				<CardListContext.Provider value={cardListContextValue}>{content}</CardListContext.Provider>
			</SprintLifecycleContext.Provider>
		);
	}

	return content;
};

export default CardListContainer;
